import { useLayoutEffect } from "react";
import { LandmarkIcon, SquarePenIcon } from "lucide-react";

import { COMMAND_ID as SELECT_ENTITY_COMMAND_ID } from "@/app/app/_/commands/SelectEntityCommand";
import { Icon } from "@/components/atoms/Icon";
import {
  NavigableCardGrid,
  navigableCardSelectionClassName,
} from "@/components/molecules/NavigableCardGrid";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { State } from "@/state";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const COMMAND_ID = "add-bank-account";

const CREATE_BANK_ACCOUNT_COMMAND_ID = "create-FinancialAccountObject";
const CONNECT_BANK_ACCOUNT_COMMAND_ID = "connect-bank-account";

export type AddBankAccountContextType = Record<string, never>;

type Option = {
  id: "plaid" | "manual";
  title: string;
  description: string;
  ReactIcon: typeof LandmarkIcon;
  onClick: () => void;
};

const OPTIONS: Option[] = [
  {
    id: "plaid",
    title: "Connect via Plaid",
    description: "Live balance and transaction sync from your bank.",
    ReactIcon: LandmarkIcon,
    onClick: () => {
      // Pre-select entity before mounting ``connect-bank-account`` so
      // ``usePlaidLink`` only initializes once (see ConnectAccountSource).
      State.getState().pushToCommandStack({
        commandDefinitionId: SELECT_ENTITY_COMMAND_ID,
        context: {
          onSubmit: (entity) => {
            State.getState().pushToCommandStack({
              commandDefinitionId: CONNECT_BANK_ACCOUNT_COMMAND_ID,
              context: { entityIdentifier: entity.identifier },
            });
          },
        },
      });
    },
  },
  {
    id: "manual",
    title: "Add manually",
    description:
      "Enter transit, routing, and account numbers without connecting Plaid.",
    ReactIcon: SquarePenIcon,
    onClick: () => {
      State.getState().pushToCommandStack({
        commandDefinitionId: SELECT_ENTITY_COMMAND_ID,
        context: {
          onSubmit: (entity) => {
            State.setState({
              scopedEntityIdentifier: entity.identifier,
            });
            State.getState().pushToCommandStack({
              commandDefinitionId: CREATE_BANK_ACCOUNT_COMMAND_ID,
              context: { entityIdentifier: entity.identifier },
            });
          },
        },
      });
    },
  },
];

function Component() {
  return (
    <StickyLayout>
      <div className="flex flex-col gap-1">
        <Typography
          level="body-sm"
          className="pl-4 pr-4 pt-4 text-muted-foreground"
        >
          How do you want to add a bank account?
        </Typography>
        <NavigableCardGrid
          itemCount={OPTIONS.length}
          minColumnWidth={280}
          getItemKey={(index) => OPTIONS[index].id}
          onActivate={(index) => OPTIONS[index].onClick()}
          renderItem={({ index, isActive, isFocusWithin, rootProps }) => {
            const option = OPTIONS[index];
            return (
              <Card
                {...rootProps}
                className={cn(
                  rootProps.className,
                  "cursor-pointer h-full",
                  navigableCardSelectionClassName({ isActive, isFocusWithin }),
                )}
              >
                <CardHeader>
                  <Icon ReactIcon={option.ReactIcon} size="lg" />
                  <CardTitle>{option.title}</CardTitle>
                  <CardDescription>{option.description}</CardDescription>
                </CardHeader>
              </Card>
            );
          }}
        />
      </div>
    </StickyLayout>
  );
}

/**
 * Chooser for adding a bank account — Plaid connect or manual entry.
 *
 * Opened from the Financial Accounts ``+`` button, the Create menu's
 * bank account item, and the command palette. Manual create still uses
 * ``create-FinancialAccountObject``; Plaid uses ``connect-bank-account``.
 */
export function AddBankAccountCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Add Bank Account",
      getLabel: () => "Add Bank Account",
      description: "Connect via Plaid or add a bank account manually",
      component: Component,
      onClick: () => {
        State.getState().pushToCommandStack({
          commandDefinitionId: COMMAND_ID,
        });
      },
      ReactIcon: LandmarkIcon,
      group: "Integrations",
    });

    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, []);

  return null;
}
